import clsx from 'clsx';
import * as React from 'react';
import clickOutsideStore from '../../../ClickOutsideStore';
import { T, TPlainTextKey } from '../../../Translations';

export type ActionTone = 'default' | 'primary' | 'muted';

export interface StatusAction {
  key: string;
  label: TPlainTextKey;
  tone: ActionTone;
  // Status changes keep the menu open so the result lands in place and the
  // options re-read against the new state. Only actions that take you somewhere
  // else close it.
  closeOnSelect: boolean;
  onSelect: () => void;
}

interface Props {
  // The chip itself is the trigger, so status and the actions that change it
  // live in one control rather than being split across the list and the editor.
  chip: React.ReactNode;
  actions: ReadonlyArray<StatusAction>;
  disabled: boolean;
  label: string;
  intercomTarget: string;
  align?: 'left' | 'right';
}

const toneClassName = (tone: ActionTone): string => {
  switch (tone) {
    case 'primary':
      return 'text-foreground';
    case 'muted':
      return 'text-muted-foreground';
    case 'default':
      return 'text-foreground';
  }
};

const StatusMenu: React.FC<Props> = ({
  chip,
  actions,
  disabled,
  label,
  intercomTarget,
  align = 'right',
}) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const close = React.useCallback(() => setOpen(false), []);

  // Registered when the menu opens, the same way ActionMenu does it: an effect
  // here would be state standing in for an event.
  const openMenu = () => {
    clickOutsideStore.addListener({ ref, callback: close });
    setOpen(true);
  };

  // Nothing to choose from: render the chip as plain status.
  if (actions.length === 0) {
    return <span className="flex items-center justify-end min-w-16 px-1 py-2.5">{chip}</span>;
  }

  return (
    <div className="relative flex" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            close();
          }
        }}
        className="flex items-center justify-end min-w-16 px-1 py-2.5 hover:opacity-80 transition-opacity disabled:opacity-50"
        data-intercom-target={intercomTarget}
      >
        {chip}
      </button>
      {open && (
        <div
          role="menu"
          className={clsx(
            'absolute z-30 top-full w-60 flex flex-col rounded-md border border-border bg-card py-1',
            'shadow-[0px_6px_20px_-2px_rgba(26,26,26,0.14),0px_4px_6px_rgba(26,26,26,0.06)]',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {actions.map((action) => (
            <button
              key={action.key}
              type="button"
              role="menuitem"
              disabled={disabled}
              className={clsx(
                'px-3 py-2 text-left hover:bg-muted transition-colors',
                'disabled:opacity-50 disabled:hover:bg-transparent',
                toneClassName(action.tone),
              )}
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight:
                  action.tone === 'primary'
                    ? 'var(--font-weight-semibold)'
                    : 'var(--font-weight-regular)',
              }}
              onClick={() => {
                if (action.closeOnSelect) {
                  close();
                }
                action.onSelect();
              }}
              data-intercom-target={`${intercomTarget}-${action.key}`}
            >
              <T kind={action.label} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatusMenu;
